// src/pages/Home/Components/FAQ.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, AlertCircle, Clock, XCircle as CloseIcon } from "lucide-react";

const faqs = [
  {
    id: 1,
    question: "چگونه می‌توانم سفارش خود را ثبت کنم؟",
    answer:
      "برای ثبت سفارش کافی است محصول مورد نظر خود را به سبد خرید اضافه کرده و سپس مراحل ثبت سفارش را طی کنید. پس از تکمیل اطلاعات و انتخاب روش ارسال، سفارش شما ثبت خواهد شد.",
  },
  {
    id: 2,
    question: "چگونه از تخفیف‌ها و پیشنهادات ویژه مطلع شوم؟",
    answer:
      "با عضویت در خبرنامه فروشگاه و دنبال کردن ما در شبکه‌های اجتماعی، از آخرین تخفیف‌ها و کدهای تخفیف اختصاصی سریع‌تر باخبر شوید.",
  },
  {
    id: 3,
    question: "آیا امکان تغییر یا لغو سفارش وجود دارد؟",
    answer:
      "بله، تا قبل از ارسال مرسوله از طریق تماس با پشتیبانی می‌توانید سفارش را لغو یا ویرایش نمایید. پس از ارسال، مطابق رویه مرجوعی عمل خواهد شد.",
  },
  {
    id: 4,
    question: "آیا محصولات با رنگ‌های مختلف موجود هستند؟",
    answer:
      "بله، اکثر محصولات چوبی و اختصاصی ما دارای کالیته رنگ متنوع هستند و در صفحه هر محصول قابل انتخاب می‌باشند.",
  },
];

// ✅ کامپوننت Toast با ترنزیشن‌های پیشرفته
const Toast = ({ message, type, onClose }) => {
  const [progress, setProgress] = useState(100);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const duration = 3500; // 3.5 ثانیه
    const interval = 50; // آپدیت هر 50 میلی‌ثانیه
    const steps = duration / interval;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const newProgress = 100 - (currentStep / steps) * 100;
      setProgress(Math.max(0, newProgress));

      if (currentStep >= steps) {
        clearInterval(timer);
        handleClose();
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 500); // زمان خروج انیمیشن
  };

  const styles = {
    success: {
      bg: "bg-gradient-to-r from-emerald-50 to-emerald-100/50",
      border: "border-emerald-400",
      icon: <CheckCircle className="w-6 h-6 text-emerald-500" />,
      text: "text-emerald-800",
      progressColor: "bg-gradient-to-r from-emerald-400 to-emerald-600",
      shadow: "shadow-emerald-500/20",
    },
    error: {
      bg: "bg-gradient-to-r from-red-50 to-red-100/50",
      border: "border-red-400",
      icon: <XCircle className="w-6 h-6 text-red-500" />,
      text: "text-red-800",
      progressColor: "bg-gradient-to-r from-red-400 to-red-600",
      shadow: "shadow-red-500/20",
    },
    info: {
      bg: "bg-gradient-to-r from-blue-50 to-blue-100/50",
      border: "border-blue-400",
      icon: <AlertCircle className="w-6 h-6 text-blue-500" />,
      text: "text-blue-800",
      progressColor: "bg-gradient-to-r from-blue-400 to-blue-600",
      shadow: "shadow-blue-500/20",
    },
  };

  const currentStyle = styles[type] || styles.success;

  return (
    <div 
      className={`fixed top-6 right-6 z-[10000] transition-all duration-500 ${
        isExiting 
          ? "translate-x-full opacity-0 scale-95" 
          : "translate-x-0 opacity-100 scale-100"
      }`}
    >
      <div
        className={`flex items-center gap-4 px-5 py-4 rounded-2xl shadow-2xl border-r-[5px] ${currentStyle.bg} ${currentStyle.border} backdrop-blur-md min-w-[320px] max-w-md transition-all duration-300 hover:scale-[1.02] hover:shadow-3xl ${currentStyle.shadow}`}
      >
        {/* آیکون با انیمیشن چرخش */}
        <div className="flex-shrink-0 animate-bounce-in">
          {currentStyle.icon}
        </div>

        {/* متن پیام */}
        <div className="flex-1">
          <p className={`text-sm font-bold ${currentStyle.text} leading-relaxed`}>
            {message}
          </p>
          {/* زمان باقی‌مانده */}
          <div className="flex items-center gap-1 mt-1">
            <Clock className="w-3 h-3 text-gray-400" />
            <span className="text-[10px] text-gray-400">
              {Math.ceil((progress / 100) * 3.5)} ثانیه
            </span>
          </div>
        </div>

        {/* دکمه بستن با انیمیشن */}
        <button
          onClick={handleClose}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 hover:rotate-90 transition-all duration-300 hover:scale-110"
        >
          <CloseIcon className="w-5 h-5" />
        </button>
      </div>

      {/* نوار پیشرفت با انیمیشن نرم */}
      <div className="mt-2 h-1.5 rounded-full bg-gray-200/70 overflow-hidden shadow-inner">
        <div 
          className={`h-full ${currentStyle.progressColor} rounded-full transition-all duration-100 ease-linear`}
          style={{ 
            width: `${progress}%`,
            boxShadow: '0 0 10px rgba(16, 185, 129, 0.3)'
          }}
        >
          {/* افکت درخشش روی نوار */}
          <div className="w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
        </div>
      </div>
    </div>
  );
};

const FAQ = () => {
  const [question, setQuestion] = useState("");
  const [openId, setOpenId] = useState(null);
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();

  const handleViewAll = () => {
    navigate("/faq");
  };

  const handleQuestionSubmit = (e) => {
    e.preventDefault();
    if (!question.trim()) {
      setToast({
        message: "لطفا سوال خود را وارد کنید!",
        type: "error",
      });
      return;
    }

    setToast({
      message: "سوال شما با موفقیت ثبت شد! ✅",
      type: "success",
    });
    
    setQuestion("");
  };

  const closeToast = () => {
    setToast(null);
  };

  return (
    <section className="w-full py-8 sm:py-12 px-3 sm:px-4" dir="rtl">
      {/* نمایش Toast */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={closeToast} 
        />
      )}

      {/* باکس اصلی */}
      <div className="relative max-w-5xl mx-auto rounded-[28px] border border-[#8A9A7B]/40 bg-white/70 backdrop-blur-sm p-4 sm:p-6 md:p-8 lg:p-10 shadow-sm">
        
        {/* نشان (Badge) بالای کادر */}
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
          <span className="bg-[#8A9A7B] text-white text-[10px] sm:text-xs lg:text-sm font-medium px-4 sm:px-6 py-1.5 rounded-full shadow-sm tracking-wide whitespace-nowrap">
            سوالات متداول
          </span>
        </div>

        {/* بدنه دو ستونه */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 items-stretch pt-2">
          
          {/* ستون اول: باکس فرم "هنوز سوال دارید؟" */}
          <div className="lg:col-span-4 bg-[#F7F7F6] rounded-2xl p-4 sm:p-5 lg:p-6 flex flex-col items-center justify-between border border-[#EAEAE8] text-center">
            <div>
              <h3 className="text-sm sm:text-base lg:text-lg font-black text-[#333333] mb-1.5">
                هنوز سوال دارید؟
              </h3>
              <p className="text-[10px] sm:text-xs text-[#737373] mb-3 sm:mb-4">
                تیم پشتیبانی ما آماده پاسخگویی به شماست
              </p>
            </div>

            {/* تکست‌اریا */}
            <div className="w-full flex-1 min-h-[100px] sm:min-h-[120px] lg:min-h-[140px] mb-3 sm:mb-4">
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="سوالتان را اینجا برای ما بگذارید..."
                className="w-full h-full p-3 text-[11px] sm:text-xs text-white placeholder-white/70 bg-[#94A385] rounded-xl resize-none outline-none focus:ring-2 focus:ring-[#8A9A7B] transition-all"
              />
            </div>

            {/* دکمه ثبت سوال */}
            <button
              onClick={handleQuestionSubmit}
              type="button"
              className="w-28 sm:w-32 py-1.5 sm:py-2 text-[11px] sm:text-xs font-bold text-white bg-[#8A9A7B] hover:bg-[#7D8D6E] rounded-lg transition-colors shadow-sm active:scale-95 cursor-pointer"
            >
              ثبت سوال
            </button>
          </div>

          {/* ستون دوم: لیست سوالات آکاردئونی */}
          <div className="lg:col-span-8 flex flex-col justify-between gap-3">
            <div className="flex flex-col gap-2 sm:gap-3">
              {faqs.map((faq) => {
                const isOpen = openId === faq.id;
                return (
                  <div
                    key={faq.id}
                    className="rounded-xl bg-[#F4F1EA] border border-[#EBE7DE] overflow-hidden transition-all duration-200"
                  >
                    <button
                      onClick={() => setOpenId(isOpen ? null : faq.id)}
                      className="w-full flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 lg:py-3.5 text-right transition-colors cursor-pointer group"
                    >
                      <span className="text-[#8A9A7B] text-base sm:text-lg font-bold order-2 transition-transform duration-300 group-hover:scale-110 flex-shrink-0">
                        {isOpen ? "−" : "+"}
                      </span>

                      <span className="text-[11px] sm:text-xs lg:text-[13px] font-bold text-[#2E2E2E] order-1 pr-2 sm:pr-3">
                        {faq.question}
                      </span>
                    </button>

                    <div
                      className={`grid transition-all duration-300 ease-in-out px-3 sm:px-4 text-[#555] text-[10px] sm:text-xs leading-5 sm:leading-6 ${
                        isOpen
                          ? "grid-rows-[1fr] opacity-100 pb-3 sm:pb-3.5 pt-0.5 sm:pt-1"
                          : "grid-rows-[0fr] opacity-0 pb-0"
                      }`}
                    >
                      <div className="overflow-hidden">{faq.answer}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* دکمه "مشاهده همه سوالات" */}
            <button
              onClick={handleViewAll}
              className="w-full py-2.5 sm:py-3 mt-1 rounded-xl bg-[#F4F1EA] hover:bg-[#EBE7DE] border border-[#EBE7DE] text-[#8A9A7B] text-[11px] sm:text-xs lg:text-sm font-bold flex items-center justify-center gap-2 transition-all cursor-pointer group shadow-sm active:scale-[0.99]"
            >
              <span>مشاهده همه سوالات</span>
              <span className="transition-transform duration-200 group-hover:-translate-x-1">
                ←
              </span>
            </button>
          </div>

        </div>
      </div>
    </section>
  );
};

export default FAQ;